import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, FileText, Pause, Play } from "lucide-react";
import { ALBUM_COVER, LINKS, TRACKS } from "@/data";
import {
  AppleMusicIcon,
  SpotifyIcon,
  YouTubeIcon,
  YouTubeMusicIcon,
} from "@/components/PlatformIcons";
import TrackShare from "@/components/TrackShare";
import { PREVIEW_PLAY_EVENT, TRACKLIST_PLAY_EVENT } from "@/components/RandomPreview";

const PLATFORMS = [
  {
    name: "Spotify",
    href: LINKS.spotify,
    Icon: SpotifyIcon,
    testid: "platform-link-spotify",
  },
  {
    name: "Apple Music",
    href: LINKS.appleMusic,
    Icon: AppleMusicIcon,
    testid: "platform-link-apple-music",
  },
  {
    name: "YouTube Music",
    href: LINKS.youtubeMusic,
    Icon: YouTubeMusicIcon,
    testid: "platform-link-youtube-music",
  },
  {
    name: "YouTube",
    href: LINKS.youtube,
    Icon: YouTubeIcon,
    testid: "platform-link-youtube",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 },
  }),
};

export default function AlbumLaunch() {
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [openLyrics, setOpenLyrics] = useState(null);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    const onTime = () =>
      setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
    };
    const onPreview = () => {
      audio.pause();
      setPlaying(false);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);
    window.addEventListener(PREVIEW_PLAY_EVENT, onPreview);
    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
      window.removeEventListener(PREVIEW_PLAY_EVENT, onPreview);
    };
  }, []);

  const toggleTrack = (index) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (current === index) {
      if (playing) {
        audio.pause();
        setPlaying(false);
      } else {
        window.dispatchEvent(new CustomEvent(TRACKLIST_PLAY_EVENT));
        audio.play();
        setPlaying(true);
      }
      return;
    }
    window.dispatchEvent(new CustomEvent(TRACKLIST_PLAY_EVENT));
    setCurrent(index);
    setProgress(0);
    audio.src = TRACKS[index].preview;
    audio.play();
    setPlaying(true);
  };

  return (
    <section
      id="album"
      data-testid="album-launch-section"
      className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36"
    >
      <div className="pointer-events-none absolute left-0 top-24 h-96 w-96 bg-[radial-gradient(circle,rgba(200,16,46,0.14),transparent_65%)] blur-3xl" />

      <motion.p
        variants={reveal}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        className="font-mono-tac text-xs uppercase tracking-[0.35em] text-red-600"
      >
        01 — L'uscita
      </motion.p>
      <motion.h2
        variants={reveal}
        custom={1}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        className="font-display mt-4 text-5xl uppercase leading-none text-neutral-100 sm:text-7xl"
      >
        Linea Cremisi <span className="text-[#C8102E]">EP</span>
      </motion.h2>
      <motion.p
        variants={reveal}
        custom={2}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400"
      >
        Sei tracce da ascoltare guardando la pioggia da un finestrino. Dal 6
        ottobre 2026, ovunque ascolti musica.
      </motion.p>

      <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="relative"
        >
          <div className="absolute -inset-6 -z-10 bg-[radial-gradient(ellipse_at_50%_0%,rgba(200,16,46,0.3),transparent_60%)] blur-xl" />
          <div className="group relative border border-red-900/40 bg-[#111] p-3 transition-colors duration-300 hover:border-red-600/70">
            <div className="overflow-hidden">
              <img
                src={ALBUM_COVER}
                alt="Copertina ufficiale — Linea Cremisi EP"
                data-testid="album-cover-image"
                className="aspect-square w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>
            <div className="mt-3 flex items-center justify-between px-1 pb-1">
              <span className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                EP · 2026 · 6 tracce
              </span>
              <span className="font-serif-logo text-xs tracking-[0.3em] text-red-600">
                ◆
              </span>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {PLATFORMS.map((p) => (
              <a
                key={p.name}
                data-testid={p.testid}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 border border-neutral-800 bg-[#0D0D0D] px-5 py-3 font-mono-tac text-xs uppercase tracking-[0.2em] text-neutral-300 transition-all duration-200 hover:border-red-600 hover:text-red-500 hover:shadow-[0_0_20px_rgba(200,16,46,0.35)]"
              >
                <p.Icon className="h-4 w-4" />
                {p.name}
              </a>
            ))}
          </div>
        </motion.div>

        <div>
          <motion.div
            variants={reveal}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            data-testid="album-tracklist"
            className="border border-neutral-800/80 bg-[#0D0D0D]"
          >
            <div className="border-b border-neutral-800/80 px-5 py-3">
              <span className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Tracklist — play per un assaggio di 30", testo per le parole
              </span>
            </div>
            {TRACKS.map((track, i) => {
              const active = current === i;
              const lyricsOpen = openLyrics === i;
              return (
                <div
                  key={track.title}
                  data-testid={`track-row-${i + 1}`}
                  className={`relative border-b border-neutral-800/50 last:border-b-0 ${
                    active ? "bg-red-950/20" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 px-5 py-4 sm:gap-3">
                    <button
                      data-testid={`track-play-${i + 1}`}
                      onClick={() => toggleTrack(i)}
                      className="group flex min-w-0 flex-1 items-center gap-4 text-left"
                      aria-label={`${active && playing ? "Pausa" : "Ascolta"} ${track.title}`}
                    >
                      <span
                        className={`font-mono-tac text-xs ${
                          active ? "text-red-500" : "text-neutral-600"
                        }`}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-neutral-700 text-neutral-400 transition-colors duration-200 group-hover:border-red-600 group-hover:text-red-500">
                        {active && playing ? (
                          <Pause className="h-3.5 w-3.5 fill-current" />
                        ) : (
                          <Play className="h-3.5 w-3.5 fill-current" />
                        )}
                      </span>
                      <span
                        className={`min-w-0 flex-1 truncate text-base font-medium tracking-wide transition-colors duration-200 group-hover:text-red-400 ${
                          active ? "text-red-400" : "text-neutral-200"
                        }`}
                      >
                        {track.title}
                      </span>
                      {active && playing && (
                        <span className="flex h-4 items-end gap-[3px]">
                          {[0, 1, 2].map((b) => (
                            <span
                              key={b}
                              className="eq-bar w-[3px] bg-red-500"
                              style={{
                                height: "100%",
                                animationDelay: `${b * 0.15}s`,
                              }}
                            />
                          ))}
                        </span>
                      )}
                      <span className="font-mono-tac text-xs text-neutral-500">
                        {track.duration}
                      </span>
                    </button>
                    <button
                      data-testid={`track-lyrics-toggle-${i + 1}`}
                      onClick={() => setOpenLyrics(lyricsOpen ? null : i)}
                      aria-expanded={lyricsOpen}
                      className={`flex shrink-0 items-center gap-1.5 border px-2.5 py-1.5 font-mono-tac text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                        lyricsOpen
                          ? "border-red-600 text-red-500"
                          : "border-neutral-800 text-neutral-500 hover:border-red-700 hover:text-red-500"
                      }`}
                    >
                      <FileText className="h-3 w-3" />
                      <span className="hidden sm:inline">Testo</span>
                      <ChevronDown
                        className={`h-3 w-3 transition-transform duration-300 ${
                          lyricsOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <TrackShare track={track} index={i} />
                  </div>
                  <AnimatePresence initial={false}>
                    {lyricsOpen && (
                      <motion.div
                        key="lyrics"
                        data-testid={`track-lyrics-${i + 1}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-red-950/40 bg-[#090909] px-5 py-6 sm:px-14">
                          <p className="font-mono-tac mb-4 text-[10px] uppercase tracking-[0.3em] text-red-600">
                            Testo — {track.title}
                          </p>
                          <p className="font-lyric whitespace-pre-line text-lg italic leading-relaxed text-neutral-300">
                            {track.lyrics}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {active && (
                    <span
                      className="absolute bottom-0 left-0 h-[2px] bg-[#C8102E] transition-none"
                      style={{ width: `${progress * 100}%` }}
                    />
                  )}
                </div>
              );
            })}
          </motion.div>

          <motion.div
            variants={reveal}
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="mt-8 overflow-hidden border border-neutral-800/80"
          >
            <iframe
              data-testid="spotify-player-iframe"
              title="Spotify — Linea Cremisi EP"
              src={LINKS.spotifyEmbed}
              width="100%"
              height="352"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="block"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
